import { AnnouncementsCard, NewAnnouncementBanner } from '@backstage-community/plugin-announcements';
import { Content, InfoCard, Link, LinkButton, Page } from '@backstage/core-components';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { NewReleaseNoteBanner, ReleaseNotesCard } from '@internal/plugin-release-notes';
import { Card, Grid, Link as ExternalLink, Theme, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import BugReportOutlinedIcon from '@material-ui/icons/BugReportOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { FavoritesMenu } from './FavoritesMenu';

const GET_STARTED_PATH = '/docs/default/component/developer-corner/DevHub/1_Info/portal_info';
const PLATFORM_TEAM_PATH = '/catalog/default/group/it_azure_platformteam_group';
const SNOW_SELF_SERVICE_URL =
  'https://cae.service-now.com/esc?id=emp_taxonomy_topic&topic_id=f46e947c839a8bd011af30e81bda1e15';
const SNOW_REPORT_ISSUE_URL =
  'https://cae.service-now.com/esc?id=sc_cat_item&sys_id=e77a3d484737de109c4bd8ca216d4333';

const useStyles = makeStyles((theme: Theme) => {
  // The hero reuses the active design's header artwork; themes without one
  // (Classic) fall back to a CAE blue gradient.
  const pageBackground = theme.page?.backgroundImage;
  const hasPageBanner = Boolean(pageBackground) && pageBackground !== 'unset' && pageBackground !== 'none';
  const heroColor = hasPageBanner ? theme.page.fontColor : '#FFFFFF';

  return {
    content: {
      '&&': {
        paddingTop: theme.spacing(2),
      },
    },
    banners: {
      '&:not(:empty)': {
        marginBottom: theme.spacing(2),
      },
    },
    hero: {
      position: 'relative',
      overflow: 'hidden',
      color: heroColor,
      backgroundColor: '#163984',
      backgroundImage: hasPageBanner
        ? pageBackground
        : 'linear-gradient(120deg, #0A1F44 0%, #163984 45%, #2969F2 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      padding: theme.spacing(4),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3),
      },
    },
    heroTop: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
    },
    eyebrow: {
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      fontWeight: 700,
      opacity: 0.85,
    },
    heroTitle: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
      marginTop: theme.spacing(0.5),
    },
    heroText: {
      maxWidth: 760,
      marginTop: theme.spacing(1.5),
      fontSize: '1rem',
      lineHeight: 1.6,
      opacity: 0.92,
    },
    heroActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(3),
    },
    primaryAction: {
      backgroundColor: '#FFFFFF',
      color: '#0A1F44',
      '&:hover': {
        backgroundColor: alpha('#FFFFFF', 0.88),
      },
    },
    outlineAction: {
      color: heroColor,
      borderColor: alpha(heroColor, 0.6),
      '&:hover': {
        borderColor: heroColor,
        backgroundColor: alpha(heroColor, 0.1),
      },
    },
    assistIntro: {
      marginBottom: theme.spacing(2),
    },
    assistItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
      height: '100%',
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
    },
    assistIcon: {
      fontSize: 32,
    },
    assistTitle: {
      fontWeight: 700,
    },
  };
});

export const HomePage = () => {
  const classes = useStyles();

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content className={classes.content}>
          <div className={classes.banners}>
            <NewAnnouncementBanner />
            <NewReleaseNoteBanner />
          </div>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card className={classes.hero}>
                <div className={classes.heroTop}>
                  <div>
                    <Typography variant="overline" className={classes.eyebrow}>
                      CAE Internal Developer Portal
                    </Typography>
                    <Typography variant="h3" component="h1" className={classes.heroTitle}>
                      Welcome to DevHub
                    </Typography>
                  </div>
                  <FavoritesMenu className={classes.outlineAction} />
                </div>
                <Typography className={classes.heroText}>
                  Your platform for building software at CAE. Find the tools, templates, tutorials and documentation
                  that support every phase of your development journey, streamline your workflow and help you reach
                  new heights in your projects.
                </Typography>
                <div className={classes.heroActions}>
                  <LinkButton to={GET_STARTED_PATH} variant="contained" className={classes.primaryAction}>
                    Get Started
                  </LinkButton>
                  <LinkButton to="/search" variant="outlined" className={classes.outlineAction}>
                    Search DevHub
                  </LinkButton>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <AnnouncementsCard title="Announcements" max={3} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReleaseNotesCard title="Release Notes" max={3} />
            </Grid>
            <Grid item xs={12}>
              <InfoCard title="Platform Engineering Team Assistance">
                <Typography className={classes.assistIntro}>
                  The <Link to={PLATFORM_TEAM_PATH}>Platform Engineering Team</Link> offers a whole catalog of
                  self-service request tickets on ServiceNow (SNOW) that you can use to submit assistance requests.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <div className={classes.assistItem}>
                      <HelpOutlineIcon color="primary" className={classes.assistIcon} />
                      <div>
                        <Typography variant="subtitle1" className={classes.assistTitle}>
                          Need assistance?
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          You may seek help from our{' '}
                          <ExternalLink href={SNOW_SELF_SERVICE_URL} target="_blank" rel="noopener noreferrer">
                            Self Service SNOW request forms
                          </ExternalLink>
                          .
                        </Typography>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <div className={classes.assistItem}>
                      <BugReportOutlinedIcon color="primary" className={classes.assistIcon} />
                      <div>
                        <Typography variant="subtitle1" className={classes.assistTitle}>
                          Found an issue?
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Let us know by{' '}
                          <ExternalLink href={SNOW_REPORT_ISSUE_URL} target="_blank" rel="noopener noreferrer">
                            reporting an issue on SNOW
                          </ExternalLink>
                          .
                        </Typography>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </InfoCard>
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
